import React from "react";

import { makeStyles, Checkbox, FormControlLabel, QuoteUrgency, TextInput } from "@portex-pro/ui-components";
import { quoteRequestActions } from "app/pages/quote-request/_store/quoteRequestSliceV2";
import clsx from "clsx";
import find from "lodash/find";
import { getUrgencyOptions, UrgencyOption } from "pages/shipper/pages/request-quote/pages/ltl/utils/getUrgencyOptions";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toLuxonRelative } from "utils/toLuxonRelative";

const useStyles = makeStyles(() => ({
  urgency: {
    "& .MuiBox-root": {
      justifyContent: "space-between",
      width: "100%",
      height: "30px",
    },
  },
}));

export const ReferenceNumberSection: React.FC = () => {
  const urgencyOptions = getUrgencyOptions();

  const [selectedUrgency, setSelectedUrgency] = React.useState<UrgencyOption | null>(null);

  const { urgency: urgencyStyle } = useStyles();

  const { t } = useTranslation(["common", "quoteRequest", "shipper"]);
  const { control } = useFormContext();
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();

  const renderUrgencyLabel = (id: string | undefined) => {
    const option = find(urgencyOptions, { id });
    const fallback = toLuxonRelative(selectedUrgency?.value);

    return option
      ? t("shipper:urgencyLabel", {
          type: "Quote",
          label: option.label,
        })
      : t("shipper:urgencyLabel", {
          type: "Quote",
          label: fallback,
        });
  };

  return (
    <div className="pt-4 mb-6 max-w-[70%]">
      <h3 className="text-[16px] text-gray-500 font-semibold mb-2">
        {t("quoteRequest:CargoStep.ReferenceNumberSection.title")}
      </h3>
      <div className="flex w-full items-start justify-between gap-4">
        <Controller
          name="reference_number"
          control={control}
          render={({ field, fieldState }) => (
            <div className="flex-1">
              <label className="block text-md font-bold mb-1">
                {t("quoteRequest:CargoStep.ReferenceNumberSection.referenceNumberLabel")}
              </label>
              <TextInput
                value={field.value}
                onChange={(e) => setQuoteRequestAction({ reference_number: e.target.value })}
                fullWidth
                placeholder={t("quoteRequest:CargoStep.ReferenceNumberSection.referenceNumberPlaceholder")}
                className="w-full px-2 py-2"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            </div>
          )}
        />
        <Controller
          name="internal_reference_number"
          control={control}
          render={({ field, fieldState }) => (
            <div className="flex-1">
              <label className="block text-md font-bold mb-1">
                {t("shipper:shipmentDetailsStep.internalReferenceNumber")}
              </label>
              <TextInput
                value={field.value ?? ""}
                onChange={(e) => setQuoteRequestAction({ internal_reference_number: e.target.value })}
                fullWidth
                className="w-full px-2 py-2"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            </div>
          )}
        />
        <div className="flex-1">
          <label className="block text-md font-bold mb-1">
            {t("quoteRequest:CargoStep.ReferenceNumberSection.urgencyLabel")}
          </label>
          <Controller
            name="deadline_respond_at"
            control={control}
            render={({ fieldState }) => (
              <div className="flex flex-col gap-1">
                <QuoteUrgency
                  className={clsx("p-2 mt-1 w-full flex justify-between", urgencyStyle)}
                  data={urgencyOptions}
                  getLabel={renderUrgencyLabel}
                  value={selectedUrgency?.id}
                  onChange={(id) => {
                    const selected = find(urgencyOptions, { id });

                    setSelectedUrgency(selected ?? null);
                    setQuoteRequestAction({ deadline_respond_at: selected?.value.toISO() });
                  }}
                  onClear={() => {
                    setSelectedUrgency(null);
                    setQuoteRequestAction({ deadline_respond_at: null });
                  }}
                />
                {fieldState.error && (
                  <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">{fieldState.error.message}</p>
                )}
              </div>
            )}
          />
        </div>
      </div>
      <div className="mt-2">
        <Controller
          name="hide_internal_ref_till_dispatch"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    setQuoteRequestAction({ hide_internal_ref_till_dispatch: e.target.checked });
                  }}
                />
              }
              label={t("shipper:shipmentDetailsStep.hideInternalRefTillDispatch")}
            />
          )}
        />
      </div>
    </div>
  );
};
